import React, { useState } from 'react'
import useModalFeedback from '../hooks/useModalFeedback'
import PhotoAttachmentPicker from '../components/PhotoAttachmentPicker'

const PROJECT_EXPENSE_CATEGORIES = ['Rental', 'Material', 'Subcon', 'Petrol', 'Others']

export default function ProjectExpenseForm() {
  const { showMessage, modalElement } = useModalFeedback()
  const [expenseCategory, setExpenseCategory] = useState('')
  const [remarks, setRemarks] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [formOpenedAt] = useState(() => Date.now())

  const addFiles = (newFiles) => {
    setSelectedFiles((prev) => [...prev, ...newFiles])
  }

  const removeFile = (fileId) => {
    setSelectedFiles(selectedFiles.filter(f => f.id !== fileId))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!expenseCategory) {
      showMessage('Please select a category (Rental, Material, Subcon, Petrol or Others)', 'Validation')
      return
    }

    if (selectedFiles.length === 0) {
      showMessage('Please select at least one file', 'Validation')
      return
    }

    try {
      setUploadingFiles(true)

      // Create FormData with all files
      const uploadFormData = new FormData()
      selectedFiles.forEach((fileObj) => {
        uploadFormData.append('files', fileObj.file)
      })
      uploadFormData.append('expense_category', expenseCategory)
      if (remarks.trim()) {
        uploadFormData.append('remarks', remarks.trim())
      }
      uploadFormData.append('submitted_at', String(formOpenedAt))
      uploadFormData.append('website', '')

      // Determine API base URL
      const apiBase = import.meta.env.VITE_API_URL ||
        (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
          ? `${window.location.origin}/api`
          : 'http://localhost:5000/api')

      // Call public upload endpoint (direct category)
      const response = await fetch(`${apiBase}/expenses/public-upload/direct`, {
        method: 'POST',
        body: uploadFormData
      })

      const data = await response.json()

      if (!response.ok) {
        console.error('Upload error response:', { status: response.status, data })
        throw new Error(data.message || data.error || `Upload failed with status ${response.status}`)
      }

      setSubmitted(true)
      setTimeout(() => {
        setSubmitted(false)
        setExpenseCategory('')
        setRemarks('')
        setSelectedFiles([])
      }, 3000)
    } catch (error) {
      console.error('Failed to upload files:', error)
      showMessage(`Upload failed: ${error.message || 'Unknown error'}`, 'Error')
    } finally {
      setUploadingFiles(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-blue-50 to-white px-3 py-4 sm:px-4 sm:py-6 lg:py-8">
      <div className="mx-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-5 sm:px-6 sm:py-6">
          <p className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
            Public Form
          </p>
          <h1 className="mt-3 text-2xl font-bold text-slate-900 sm:text-3xl">Project Expenses Form</h1>
          <p className="mt-2 text-sm text-slate-600">Submit project expense records for approval.</p>
        </div>

        {submitted ? (
          <div className="px-4 py-6 sm:px-6">
            <div className="rounded-xl border border-green-200 bg-green-50 p-4 sm:p-6 text-center">
              <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-lg font-bold text-green-700">
                OK
              </div>
              <h2 className="text-xl font-semibold text-green-700">Success!</h2>
              <p className="mt-2 text-green-700">Your {selectedFiles.length} project expense record(s) have been submitted successfully.</p>
              <p className="mt-3 text-sm text-slate-600">You can now submit another expense or close this window.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 px-4 py-5 sm:px-6 sm:py-6">
            {/* Category */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={expenseCategory}
                onChange={(e) => setExpenseCategory(e.target.value)}
                disabled={uploadingFiles}
                required
                className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
              >
                <option value="">-- Select Category --</option>
                {PROJECT_EXPENSE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Remarks */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={uploadingFiles}
                rows={3}
                placeholder="Optional notes or comments..."
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </div>

            <PhotoAttachmentPicker
              label="Upload Attachments"
              files={selectedFiles}
              onAddFiles={addFiles}
              onRemoveFile={removeFile}
              disabled={uploadingFiles}
              multiple
              allowFileUploads
            />

            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">How it works:</span> Each upload creates 1 project expense record ({PROJECT_EXPENSE_CATEGORIES.join(' / ')}) with submission date. You can upload photo or file formats such as PDF, DOCX, XLSX, PNG, JPG, and ZIP. Admin will fill in the amount and project later.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setExpenseCategory('')
                  setRemarks('')
                  setSelectedFiles([])
                }}
                disabled={uploadingFiles}
                className="h-11 rounded-lg border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[140px]"
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={uploadingFiles || selectedFiles.length === 0}
                className="h-11 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400 sm:min-w-[170px]"
              >
                {uploadingFiles ? 'Submitting...' : `Submit (${selectedFiles.length})`}
              </button>
            </div>
          </form>
        )}

        {modalElement}
      </div>
    </div>
  )
}
